// "File d'attente" — the shared queue an agent pulls work from (2026-08-11).
//
// Two lists, in the order an agent actually thinks about them: what I am
// holding right now, then what is free to take. Claiming is one tap, and the
// row leaves the shared list the moment it succeeds.
//
// Losing a race is a NORMAL outcome here, not an error: two agents can tap the
// same order within the same second, and exactly one wins (the claim is decided
// by a Postgres advisory lock + conditional update, see migration 072). So the
// loser gets a plain sentence and the list refreshes underneath them — no red
// error card for something that is simply "someone was faster".
//
// An agent never sees who holds an order they cannot take; the API does not
// send that, and this card never asks for it.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ordersApi, type QueueOrder } from '@/lib/api';
import { useT } from '@/lib/i18n';

const GOLD = '#C9A24A';

type TFn = ReturnType<typeof useT>['t'];

function waitedLabel(since: string | null, t: TFn) {
  if (!since) return '';
  const mins = Math.floor((Date.now() - new Date(since).getTime()) / 60_000);
  if (mins < 1) return t('à l’instant');
  if (mins < 60) return t('il y a {n} min', { n: mins });
  if (mins < 1440) return t('il y a {n} h', { n: Math.floor(mins / 60) });
  return t('il y a {n} j', { n: Math.floor(mins / 1440) });
}

export function OrderQueueCard() {
  const { t } = useT();
  const qc = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['order-queue'],
    queryFn: () => ordersApi.queue(),
    // Someone else may take an order at any moment, so the shared list goes
    // stale on its own rather than only on a manual reload.
    staleTime: 20_000,
    refetchInterval: 60_000,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['order-queue'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
  };

  const claim = useMutation({
    mutationFn: (id: string) => ordersApi.claim(id),
    onSuccess: () => { setMessage(null); refresh(); },
    onError: (e: Error) => { setMessage(e.message); refresh(); },
  });

  const release = useMutation({
    mutationFn: (id: string) => ordersApi.release(id),
    onSuccess: () => { setMessage(null); refresh(); },
    onError: (e: Error) => setMessage(e.message),
  });

  const mine = data?.mine ?? [];
  const available = data?.available ?? [];
  const taken = data?.taken ?? [];
  const cap = data?.cap;
  const atCap = !!cap?.enabled && cap.held >= cap.limit;

  const Row = ({ o, action }: { o: QueueOrder; action: 'claim' | 'release' }) => (
    <div
      className="px-5 py-3 border-b last:border-b-0 flex items-center gap-3"
      style={{ borderColor: 'rgba(201, 162, 74, 0.10)' }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-adm-ink)' }}>
          <Link to={`/admin/orders/${o.shopify_order_id}`} className="hover:underline">
            {o.shopify_order_name ?? o.shopify_order_id}
          </Link>
          {o.bundle && (
            <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded"
              style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}>
              {o.bundle}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {o.customer_name ?? '—'}
          <span className="text-gray-400">
            {' · '}
            {action === 'claim'
              ? waitedLabel(o.created_at, t)
              : waitedLabel(o.assignment_activity_at ?? o.claimed_at, t)}
          </span>
        </div>
      </div>
      {action === 'claim' ? (
        <button
          type="button"
          onClick={() => claim.mutate(o.shopify_order_id)}
          disabled={claim.isPending || atCap}
          className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white disabled:opacity-40 transition-opacity"
          style={{ background: GOLD }}
          title={atCap ? t('Vous avez atteint votre maximum de commandes en cours.') : undefined}
        >
          {t('Prendre en charge')}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => release.mutate(o.shopify_order_id)}
          disabled={release.isPending}
          className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold border disabled:opacity-40 text-[#6B5423] dark:text-adm-gold-ink"
          style={{ borderColor: 'rgba(201,162,74,.35)' }}
        >
          {t('Remettre dans la file')}
        </button>
      )}
    </div>
  );

  return (
    <div
      className="mb-7 bg-white rounded-xl border overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
    >
      <div className="px-5 pt-4 pb-2 flex items-center gap-3">
        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider flex-1">
          {t('File d’attente')}
        </p>
        {cap?.enabled && (
          <span className="text-[11px] font-semibold tabular-nums"
            style={{ color: atCap ? 'var(--color-status-waiting)' : 'var(--color-adm-muted)' }}>
            {cap.held}/{cap.limit}
          </span>
        )}
      </div>

      {error && (
        <p className="px-5 pb-4 text-sm" style={{ color: 'var(--color-status-stuck)' }}>
          {t('File indisponible pour le moment.')}
        </p>
      )}

      {/* A lost race, or the cap. Neutral tone — neither is a failure. */}
      {message && (
        <p className="mx-5 mb-3 px-3 py-2 rounded-lg text-xs bg-[#FEF9C3] dark:bg-status-waiting-bg text-[#854D0E] dark:text-status-waiting">
          {message}
        </p>
      )}

      {isLoading && <div className="px-5 pb-5 text-sm text-gray-400">{t('Chargement…')}</div>}

      {!isLoading && !error && (
        <>
          <div className="px-5 pb-1 text-xs font-semibold" style={{ color: 'var(--color-adm-ink)' }}>
            {t('Mes commandes en cours')} <span className="text-gray-400">({mine.length})</span>
          </div>
          {mine.length === 0 ? (
            <p className="px-5 pb-3 text-xs text-gray-500">
              {t('Vous n’avez aucune commande en cours.')}
            </p>
          ) : (
            <div className="mb-2">{mine.map((o) => <Row key={o.id} o={o} action="release" />)}</div>
          )}

          <div className="px-5 pt-2 pb-1 text-xs font-semibold border-t"
            style={{ color: 'var(--color-adm-ink)', borderColor: 'rgba(201,162,74,.12)' }}>
            {t('À prendre')} <span className="text-gray-400">({available.length})</span>
          </div>
          {available.length === 0 ? (
            <p className="px-5 pb-4 text-xs text-gray-500">
              {t('Rien à prendre pour le moment.')}
            </p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto">
              {available.map((o) => <Row key={o.id} o={o} action="claim" />)}
            </div>
          )}

          {/* Held by a colleague. Listed — greyed and with no button — so an
              order never looks lost. The name is deliberately absent: the API
              does not send it and the agent does not need it. */}
          {taken.length > 0 && (
            <>
              <div className="px-5 pt-3 pb-1 text-xs font-semibold border-t"
                style={{ color: 'var(--color-adm-muted)', borderColor: 'rgba(201,162,74,.12)' }}>
                {t('Déjà prises')} <span className="text-gray-400">({taken.length})</span>
              </div>
              <div className="max-h-[220px] overflow-y-auto">
                {taken.map((o) => (
                  <div
                    key={o.id}
                    className="px-5 py-3 border-b last:border-b-0 flex items-center gap-3 opacity-60"
                    style={{ borderColor: 'rgba(201, 162, 74, 0.10)' }}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-adm-ink)' }}>
                        {o.shopify_order_name ?? o.shopify_order_id}
                        {o.bundle && (
                          <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded"
                            style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}>
                            {o.bundle}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{o.customer_name ?? '—'}</div>
                    </div>
                    <span
                      className="shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium"
                      style={{ background: 'var(--color-adm-surface-3)', color: 'var(--color-adm-muted)' }}
                    >
                      {t('Déjà prise par un autre agent')}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="px-5 py-2.5 border-t bg-[#FAFAF8] dark:bg-adm-surface-2" style={{ borderColor: 'rgba(201,162,74,0.12)' }}>
        <p className="text-[10.5px] text-gray-500">
          {t('Une commande prise en charge et laissée sans action revient automatiquement dans la file.')}
        </p>
      </div>
    </div>
  );
}
